import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import emailjs from "@emailjs/browser";
import { Snackbar } from "@mui/material";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Section = styled.section`
  padding: 100px 5vw;
  background: ${({ theme }) => theme.bg};
`;

const Wrapper = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const Label = styled.p`
  font-family: 'Space Grotesk', monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(32px, 4vw, 48px);
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  letter-spacing: -0.02em;
  margin-bottom: 12px;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.65;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 13px 18px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text_primary};
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
    opacity: 0.6;
  }

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    background: ${({ theme }) => theme.card};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 13px 18px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text_primary};
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  outline: none;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.2s;

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
    opacity: 0.6;
  }

  &:focus {
    border-color: ${({ theme }) => theme.primary};
  }
`;

const SubmitBtn = styled.button`
  padding: 13px 32px;
  border-radius: 8px;
  border: none;
  background: ${({ theme }) => theme.primary};
  color: #07071A;
  font-size: 15px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
  align-self: flex-start;

  &:hover {
    opacity: 0.88;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Contact = () => {
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const form = useRef();
  const sectionRef = useRef();

  useEffect(() => {
    gsap.fromTo(sectionRef.current,
      { y: 40, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.8, ease: 'power3.out',
        scrollTrigger: { trigger: sectionRef.current, start: 'top 85%' }
      }
    );
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();
    setSending(true);
    emailjs
      .sendForm('service_2q9w1qg', 'template_vr5j5kb', form.current, 'ZwVfCzM1FymrLIZ7N')
      .then(() => {
        setOpen(true);
        form.current.reset();
      })
      .catch((err) => console.error(err))
      .finally(() => setSending(false));
  };

  return (
    <Section id="contact">
      <Wrapper ref={sectionRef}>
        <Header>
          <Label>Get In Touch</Label>
          <Title>Contact</Title>
          <Subtitle>
            Have a question or want to work together? Drop me a message and I'll get back to you.
          </Subtitle>
        </Header>
        <Form ref={form} onSubmit={sendEmail}>
          <Input placeholder="Your Name" name="user_name" required />
          <Input placeholder="Your Email" name="user_email" type="email" required />
          <Input placeholder="Subject" name="subject" />
          <Textarea placeholder="Your message..." rows="5" name="message" required />
          <SubmitBtn type="submit" disabled={sending}>
            {sending ? 'Sending…' : 'Send Message →'}
          </SubmitBtn>
        </Form>
        <Snackbar
          open={open}
          autoHideDuration={5000}
          onClose={() => setOpen(false)}
          message="Message sent successfully!"
        />
      </Wrapper>
    </Section>
  );
};

export default Contact;
